import { escapeBody, generateHeader, generateMethod } from 'fetch-to-curl'
import { Fragment, type ReactNode } from 'react'

// The request-side fields the cURL/HTTP snippet builders need — both the page's
// query rows and the detail fragment satisfy this structurally
export type LogRequestShape = {
  method: string
  url: string
  requestHeaders: unknown
  requestBody: unknown
}

// One JSON object per line (JSON Lines) — the same shape for the bulk export
// and the single-log download in the row detail. Each line also carries the
// request as a ready-to-run cURL command and a raw HTTP/1.1 snippet.
export const downloadLogsAsJsonl = <T extends LogRequestShape & { __typename?: string }>(
  logs: T[],
  filename: string
) => {
  const content = logs
    .map(log => {
      const { __typename, ...rest } = log
      return `${JSON.stringify({ ...rest, curl: buildCurl(log), http: buildHttp(log) })}\n`
    })
    .join('')
  const blob = new Blob([content], { type: 'application/x-ndjson' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const INDENT = '  '

// Mirrors JSON.stringify(value, null, 2) layout, but with colored tokens
export const renderJson = (value: unknown, indent: string): ReactNode => {
  if (value === null) {
    return <span className='text-purple7'>null</span>
  }

  if (typeof value === 'boolean') {
    return <span className='text-purple7'>{String(value)}</span>
  }

  if (typeof value === 'number') {
    return <span className='text-orange7'>{String(value)}</span>
  }

  if (typeof value === 'string') {
    return <span className='text-green7'>{JSON.stringify(value)}</span>
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return '[]'
    }

    const childIndent = indent + INDENT
    return (
      <>
        {'[\n'}
        {value.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Static render of a JSON snapshot, items never reorder
          <Fragment key={index}>
            {childIndent}
            {renderJson(item, childIndent)}
            {index < value.length - 1 && ','}
            {'\n'}
          </Fragment>
        ))}
        {indent}
        {']'}
      </>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value)
    if (!entries.length) {
      return '{}'
    }

    const childIndent = indent + INDENT
    return (
      <>
        {'{\n'}
        {entries.map(([key, childValue], index) => (
          <Fragment key={key}>
            {childIndent}
            <span className='text-geekblue7'>{JSON.stringify(key)}</span>
            {': '}
            {renderJson(childValue, childIndent)}
            {index < entries.length - 1 && ','}
            {'\n'}
          </Fragment>
        ))}
        {indent}
        {'}'}
      </>
    )
  }

  return <span className='text-green7'>{JSON.stringify(String(value))}</span>
}

export const getContentType = (headers: unknown) => {
  if (!headers || typeof headers !== 'object') {
    return null
  }

  const entry = Object.entries(headers as Record<string, unknown>).find(
    ([key]) => key.toLowerCase() === 'content-type'
  )
  return entry ? String(entry[1]) : null
}

// Headers as they went over the wire: the logged ones, plus the Content-Type
// axios adds implicitly when serializing a JSON body
export const buildRequestHeaders = (log: LogRequestShape) => {
  const loggedHeaders =
    log.requestHeaders && typeof log.requestHeaders === 'object'
      ? Object.fromEntries(
          Object.entries(log.requestHeaders as Record<string, unknown>).map(([key, value]) => [
            key,
            String(value),
          ])
        )
      : {}
  const isJsonBody = log.requestBody != null && typeof log.requestBody !== 'string'
  const hasContentTypeHeader = Object.keys(loggedHeaders).some(
    key => key.toLowerCase() === 'content-type'
  )

  return {
    ...loggedHeaders,
    ...(isJsonBody && !hasContentTypeHeader ? { 'Content-Type': 'application/json' } : {}),
  }
}

// Inbound webhook logs store only the path (/webhooks/rippling) — prefix it with this
// backend's origin so the snippet is runnable as-is
const apiOrigin = (() => {
  try {
    return new URL(import.meta.env.VITE_API_URL ?? '').origin
  } catch {
    return ''
  }
})()

export const getAbsoluteUrl = (url: string) => (url.startsWith('/') ? `${apiOrigin}${url}` : url)

// One chunk per curl argument (`-X GET`, `-H "..."`, `--data-binary '...'`),
// composed from fetch-to-curl helpers so shell escaping stays in the library
export const buildCurlChunks = (log: LogRequestShape) => {
  const headers = buildRequestHeaders(log)

  return [
    generateMethod({ method: log.method }),
    ...Object.entries(headers).map(
      ([key, value]) => generateHeader({ headers: { [key]: value } }).params
    ),
    // Serialized explicitly: fetch-to-curl's generateBody drops falsy bodies ('', 0, false)
    ...(log.requestBody == null
      ? []
      : [
          `--data-binary '${escapeBody(
            typeof log.requestBody === 'string' ? log.requestBody : JSON.stringify(log.requestBody)
          )}'`,
        ]),
  ].flatMap(chunk => (chunk.trim() ? [chunk.trim()] : []))
}

export const buildCurl = (log: LogRequestShape) =>
  [`curl '${escapeBody(getAbsoluteUrl(log.url))}'`, ...buildCurlChunks(log)].join(' \\\n  ')

export const buildHttpParts = (log: LogRequestShape) => {
  const url = (() => {
    try {
      return new URL(getAbsoluteUrl(log.url))
    } catch {
      return null
    }
  })()

  const headerEntries: [string, string][] = [
    ...(url ? [['Host', url.host] satisfies [string, string]] : []),
    ...Object.entries(buildRequestHeaders(log)),
  ]
  const body =
    log.requestBody == null
      ? null
      : typeof log.requestBody === 'string'
        ? log.requestBody
        : JSON.stringify(log.requestBody)

  return {
    method: log.method,
    path: url ? `${url.pathname}${url.search}` : log.url,
    headerEntries,
    body,
  }
}

export const buildHttp = (log: LogRequestShape) => {
  const { method, path, headerEntries, body } = buildHttpParts(log)

  return [
    `${method} ${path} HTTP/1.1`,
    ...headerEntries.map(([key, value]) => `${key}: ${value}`),
    ...(body != null ? ['', body] : []),
  ].join('\n')
}

export const headerEntriesOf = (headers: unknown) =>
  headers && typeof headers === 'object'
    ? Object.entries(headers as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))
    : []

// A JWT's payload is plain base64url-encoded JSON — decode it so a Bearer token header
// also shows who the token was for, its scopes and when it expires
export const decodeJwtPayload = (value: string): Record<string, unknown> | null => {
  const token = value.replace(/^Bearer\s+/i, '')
  const parts = token.split('.')
  if (parts.length !== 3 || !parts[1]) {
    return null
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))
    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0))
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

export const methodTagColors: Record<string, string> = {
  GET: 'blue',
  POST: 'green',
  PUT: 'orange',
  PATCH: 'gold',
  DELETE: 'red',
}

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} kB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
